import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { FileClock, Search, Calendar, Save, DollarSign, History, Settings } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { NumberInput } from '../../components/NumberInput';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';

const fmtCLP = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return '—';
  return num.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const s = String(iso).slice(0, 10);
    const [y, m, d] = s.split('-');
    if (!y || !m || !d) return s;
    return `${d}-${m}-${y}`;
  } catch { return iso; }
};

const diasHastaVenc = (iso) => {
  if (!iso) return null;
  try {
    const venc = new Date(String(iso).slice(0, 10));
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const diff = Math.floor((venc - hoy) / (1000 * 60 * 60 * 24));
    return diff;
  } catch { return null; }
};

export default function ChequesPorPagarPage() {
  const { user } = useAuth();
  const isAdmin = user?.rol === 'admin';
  const canEditar = ['admin', 'gerencia', 'contabilidad'].includes(user?.rol);
  const [data, setData] = useState({ items: [], totales: {}, condiciones: [] });
  const [loading, setLoading] = useState(true);
  const [incluirPagadas, setIncluirPagadas] = useState(false);
  const [search, setSearch] = useState('');
  // Editor de vencimiento
  const [editItem, setEditItem] = useState(null);
  const [editFecha, setEditFecha] = useState('');
  const [editMotivo, setEditMotivo] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  // Registrar pago cheque (iter 286.e)
  const [payItem, setPayItem] = useState(null);
  const [payForm, setPayForm] = useState({ numero_cheque: '', banco: '', fecha_emision: '', fecha_pago: '', monto: 0, observaciones: '' });
  const [savingPay, setSavingPay] = useState(false);
  // Historial (iter 286.e)
  const [histItem, setHistItem] = useState(null);
  const [histLogs, setHistLogs] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);
  // Config ventana (iter 286.e)
  const [showConfig, setShowConfig] = useState(false);
  const [ventanaDias, setVentanaDias] = useState(14);
  const [savingConfig, setSavingConfig] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/flujo-caja/cxp/cheques-por-pagar', {
        params: { incluir_pagadas: incluirPagadas },
      });
      setData(res.data || { items: [], totales: {}, condiciones: [] });
    } catch {
      toast.error('Error cargando cheques por pagar');
    } finally {
      setLoading(false);
    }
  }, [incluirPagadas]);

  useEffect(() => { load(); }, [load]);

  // Cargar config ventana al montar
  useEffect(() => {
    api.get('/flujo-caja/cxp/config/ventana-alerta-cheques')
      .then((r) => setVentanaDias(r.data?.dias || 14))
      .catch(() => {});
  }, []);

  // ─── Editor vencimiento ─────────────────────────
  const openEdit = (item) => {
    setEditItem(item);
    setEditFecha(item.fecha_vencimiento ? String(item.fecha_vencimiento).slice(0, 10) : '');
    setEditMotivo('');
  };

  const saveEdit = async () => {
    if (!editItem || !editFecha) {
      toast.error('Fecha requerida');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(editFecha)) {
      toast.error('Fecha debe ser YYYY-MM-DD');
      return;
    }
    setSavingEdit(true);
    try {
      await api.patch(`/compras/ordenes-compra/${encodeURIComponent(editItem.numero_oc)}/fecha-vencimiento`, {
        fecha_vencimiento: editFecha,
        motivo: editMotivo || '',
      });
      toast.success(`Fecha actualizada: OC ${editItem.numero_oc} → ${editFecha}`);
      setEditItem(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSavingEdit(false);
    }
  };

  // ─── Registrar pago cheque ─────────────────────────
  const openPay = (item) => {
    const hoy = new Date().toISOString().slice(0, 10);
    setPayItem(item);
    setPayForm({
      numero_cheque: '',
      banco: '',
      fecha_emision: hoy,
      fecha_pago: hoy,
      monto: item.monto || 0,
      observaciones: '',
    });
  };

  const savePay = async () => {
    if (!payItem) return;
    if (!payForm.numero_cheque.trim() || !payForm.banco.trim()) {
      toast.error('N° de cheque y banco son obligatorios');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(payForm.fecha_emision) || !/^\d{4}-\d{2}-\d{2}$/.test(payForm.fecha_pago)) {
      toast.error('Fechas deben ser YYYY-MM-DD');
      return;
    }
    if (!(Number(payForm.monto) > 0)) {
      toast.error('Monto debe ser mayor a 0');
      return;
    }
    setSavingPay(true);
    try {
      await api.post(`/flujo-caja/cxp/registrar-pago-cheque/${encodeURIComponent(payItem.numero_oc)}`, {
        ...payForm,
        numero_cheque: payForm.numero_cheque.trim(),
        banco: payForm.banco.trim(),
        monto: Number(payForm.monto),
      });
      toast.success(`Cheque #${payForm.numero_cheque} registrado`);
      setPayItem(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setSavingPay(false);
    }
  };

  // ─── Historial ─────────────────────────
  const openHist = async (item) => {
    setHistItem(item);
    setLoadingHist(true);
    try {
      const res = await api.get(`/flujo-caja/cxp/ordenes-compra/${encodeURIComponent(item.numero_oc)}/historial`);
      setHistLogs(res.data?.logs || []);
    } catch {
      setHistLogs([]);
    } finally {
      setLoadingHist(false);
    }
  };

  // ─── Config ventana ─────────────────────────
  const saveVentana = async () => {
    const n = Number(ventanaDias);
    if (!(n >= 1 && n <= 90)) {
      toast.error('Días debe estar entre 1 y 90');
      return;
    }
    setSavingConfig(true);
    try {
      await api.put('/flujo-caja/cxp/config/ventana-alerta-cheques', { dias: n });
      toast.success(`Ventana actualizada a ${n} días`);
      setShowConfig(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setSavingConfig(false);
    }
  };

  const filtered = data.items.filter((it) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (it.nombre_proveedor || '').toLowerCase().includes(q) ||
      String(it.folio || '').includes(q) ||
      String(it.numero_oc || '').includes(q) ||
      (it.rut_proveedor || '').toLowerCase().includes(q)
    );
  });

  const totales = data.totales || {};

  return (
    <div className="space-y-5" data-testid="cheques-por-pagar-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileClock className="h-6 w-6 text-blue-600" />
            Cheques por Pagar
          </h1>
          <p className="text-sm text-muted-foreground">
            Facturas cuya condición de pago es Cheque (al día, 30 o 60 días). No aparecen en la nómina de viernes.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={incluirPagadas}
              onChange={(e) => setIncluirPagadas(e.target.checked)}
              className="accent-indigo-600"
              data-testid="chk-incluir-pagadas"
            />
            Incluir pagadas
          </label>
          {canEditar && (
            <Button variant="outline" size="sm" onClick={() => setShowConfig(true)} className="gap-2" data-testid="btn-config">
              <Settings className="h-3.5 w-3.5" />
              Ventana alerta ({ventanaDias}d)
            </Button>
          )}
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardContent className="py-4">
            <div className="text-xs text-muted-foreground">Cheques pendientes</div>
            <div className="text-2xl font-bold" data-testid="stat-cantidad">{totales.cantidad || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="text-xs text-muted-foreground">Monto total</div>
            <div className="text-2xl font-bold" data-testid="stat-monto">{fmtCLP(totales.monto_total)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="text-xs text-muted-foreground">Condiciones tracked</div>
            <div className="text-sm mt-1 flex gap-1 flex-wrap">
              {(data.condiciones || []).map((c) => (
                <Badge key={c.codigo} variant="outline" className="text-xs">
                  {c.nombre}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Buscador */}
      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar proveedor, folio, OC…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
          data-testid="input-search"
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Listado ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Cargando…</div>
          ) : filtered.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No hay cheques por pagar en este momento.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Folio</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead>OC</TableHead>
                    <TableHead>Condición</TableHead>
                    <TableHead>F. Emisión</TableHead>
                    <TableHead>F. Vencimiento</TableHead>
                    <TableHead className="text-center">Días</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead className="text-center">Estado</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((it) => {
                    const dias = diasHastaVenc(it.fecha_vencimiento);
                    const badgeCls =
                      dias === null ? 'bg-slate-100 text-slate-500'
                        : dias < 0 ? 'bg-red-100 text-red-700'
                        : dias <= 7 ? 'bg-orange-100 text-orange-700'
                        : dias <= 30 ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-green-100 text-green-700';
                    return (
                      <TableRow key={it.id} data-testid={`row-cheque-${it.folio}`}>
                        <TableCell className="font-mono text-xs">{it.folio || '—'}</TableCell>
                        <TableCell className="max-w-[280px] truncate" title={it.nombre_proveedor}>
                          {it.nombre_proveedor || '—'}
                          <div className="text-[10px] text-muted-foreground">{it.rut_proveedor}</div>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{it.numero_oc || '—'}</TableCell>
                        <TableCell className="text-xs">
                          <Badge variant="outline" className="text-blue-700 border-blue-300">
                            {it.condicion_pago_nombre || '—'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{fmtDate(it.fecha_emision)}</TableCell>
                        <TableCell className="text-xs font-medium">{fmtDate(it.fecha_vencimiento)}</TableCell>
                        <TableCell className="text-center">
                          {dias !== null && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${badgeCls}`}>
                              {dias < 0 ? `${Math.abs(dias)}d vencido` : `${dias}d`}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">{fmtCLP(it.monto)}</TableCell>
                        <TableCell className="text-center">
                          {it.estado === 'pagada' ? (
                            <Badge className="bg-green-600 hover:bg-green-700">Pagada</Badge>
                          ) : it.estado === 'parcial' ? (
                            <Badge className="bg-orange-500 hover:bg-orange-600">Parcial</Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-700">Pendiente</Badge>
                          )}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {canEditar && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-1 text-green-700 border-green-300 hover:bg-green-50"
                                  onClick={() => openPay(it)}
                                  title="Registrar pago con cheque"
                                  data-testid={`btn-pay-${it.numero_oc}`}
                                >
                                  <DollarSign className="h-3.5 w-3.5" /> Pagar
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openHist(it)}
                                title="Historial de cambios"
                                data-testid={`btn-hist-${it.numero_oc}`}
                              >
                                <History className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEdit(it)}
                                title="Editar fecha (admin)"
                                data-testid={`btn-edit-venc-${it.numero_oc}`}
                              >
                                <Calendar className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                        {!isAdmin && canEditar && (
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1 text-green-700 border-green-300 hover:bg-green-50"
                                onClick={() => openPay(it)}
                                title="Registrar pago con cheque"
                                data-testid={`btn-pay-${it.numero_oc}`}
                              >
                                <DollarSign className="h-3.5 w-3.5" /> Pagar
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openHist(it)}
                                title="Historial de cambios"
                                data-testid={`btn-hist-${it.numero_oc}`}
                              >
                                <History className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                        {!canEditar && (
                          <TableCell className="text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openHist(it)}
                              title="Historial"
                              data-testid={`btn-hist-${it.numero_oc}`}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Iter 286.d — Modal editor de fecha_vencimiento (admin only) */}
      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-edit-venc">
          <DialogHeader>
            <DialogTitle>Editar fecha de vencimiento</DialogTitle>
          </DialogHeader>
          {editItem && (
            <div className="space-y-3">
              <div className="text-sm p-3 bg-slate-50 rounded-md">
                <div><b>OC:</b> {editItem.numero_oc}</div>
                <div><b>Proveedor:</b> {editItem.nombre_proveedor}</div>
                <div><b>Condición:</b> {editItem.condicion_pago_nombre}</div>
                <div><b>Vencimiento actual:</b> {editItem.fecha_vencimiento ? new Date(editItem.fecha_vencimiento).toLocaleDateString('es-CL') : '—'}</div>
              </div>
              <div>
                <Label>Nueva fecha de vencimiento *</Label>
                <Input
                  type="date"
                  value={editFecha}
                  onChange={(e) => setEditFecha(e.target.value)}
                  data-testid="input-edit-fecha"
                />
              </div>
              <div>
                <Label>Motivo del cambio</Label>
                <Textarea
                  value={editMotivo}
                  onChange={(e) => setEditMotivo(e.target.value)}
                  rows={2}
                  placeholder="Ej: Acuerdo con proveedor: pago diferido 15 días adicionales"
                  data-testid="input-edit-motivo"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  El cambio queda registrado en auditoría con tu usuario, fecha y motivo.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)} disabled={savingEdit} data-testid="btn-edit-cancel">Cancelar</Button>
            <Button onClick={saveEdit} disabled={savingEdit} className="gap-2" data-testid="btn-edit-save">
              <Save className="h-4 w-4" />
              {savingEdit ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 286.e — Modal registrar pago de cheque */}
      <Dialog open={!!payItem} onOpenChange={(o) => !o && setPayItem(null)}>
        <DialogContent className="max-w-lg" data-testid="dialog-pay-cheque">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Registrar pago con cheque
            </DialogTitle>
          </DialogHeader>
          {payItem && (
            <div className="space-y-3">
              <div className="text-sm p-3 bg-green-50 rounded-md">
                <div><b>OC:</b> {payItem.numero_oc} · <b>{payItem.condicion_pago_nombre}</b></div>
                <div><b>Proveedor:</b> {payItem.nombre_proveedor}</div>
                <div><b>Monto:</b> {fmtCLP(payItem.monto)}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>N° de Cheque *</Label>
                  <Input
                    value={payForm.numero_cheque}
                    onChange={(e) => setPayForm({ ...payForm, numero_cheque: e.target.value })}
                    placeholder="Ej: 123456"
                    data-testid="input-pay-numero"
                  />
                </div>
                <div>
                  <Label>Banco *</Label>
                  <Input
                    value={payForm.banco}
                    onChange={(e) => setPayForm({ ...payForm, banco: e.target.value })}
                    placeholder="Ej: Banco de Chile"
                    data-testid="input-pay-banco"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Fecha emisión *</Label>
                  <Input
                    type="date"
                    value={payForm.fecha_emision}
                    onChange={(e) => setPayForm({ ...payForm, fecha_emision: e.target.value })}
                    data-testid="input-pay-emision"
                  />
                </div>
                <div>
                  <Label>Fecha pago (efectivo) *</Label>
                  <Input
                    type="date"
                    value={payForm.fecha_pago}
                    onChange={(e) => setPayForm({ ...payForm, fecha_pago: e.target.value })}
                    data-testid="input-pay-fecha-pago"
                  />
                </div>
              </div>
              <div>
                <Label>Monto *</Label>
                <NumberInput
                  allowDecimal={false}
                  allowNegative={false}
                  value={payForm.monto}
                  onChange={(clean) => setPayForm({ ...payForm, monto: clean })}
                  data-testid="input-pay-monto"
                />
              </div>
              <div>
                <Label>Observaciones</Label>
                <Textarea
                  value={payForm.observaciones}
                  onChange={(e) => setPayForm({ ...payForm, observaciones: e.target.value })}
                  rows={2}
                  placeholder="Opcional"
                  data-testid="input-pay-obs"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayItem(null)} disabled={savingPay}>Cancelar</Button>
            <Button onClick={savePay} disabled={savingPay} className="bg-green-600 hover:bg-green-700 gap-2" data-testid="btn-pay-save">
              <Save className="h-4 w-4" />
              {savingPay ? 'Guardando…' : 'Registrar pago'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 286.e — Modal historial */}
      <Dialog open={!!histItem} onOpenChange={(o) => !o && setHistItem(null)}>
        <DialogContent className="max-w-2xl" data-testid="dialog-historial">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-600" />
              Historial de la OC {histItem?.numero_oc}
            </DialogTitle>
          </DialogHeader>
          {loadingHist ? (
            <div className="text-center py-6 text-sm text-muted-foreground">Cargando…</div>
          ) : histLogs.length === 0 ? (
            <div className="text-center py-6 text-sm text-muted-foreground">
              No hay eventos registrados en el historial de esta OC.
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {histLogs.map((log, i) => (
                <div key={i} className="border-l-4 border-blue-400 pl-3 py-1 text-sm">
                  <div className="flex justify-between items-center">
                    <Badge variant="outline" className="text-xs">{log.accion}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {log.timestamp ? new Date(log.timestamp).toLocaleString('es-CL') : '—'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 mt-1">{log.detalle}</div>
                  <div className="text-[10px] text-muted-foreground">por {log.usuario}</div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistItem(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 286.e — Modal config ventana alerta */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-md" data-testid="dialog-config-ventana">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-slate-600" />
              Configurar ventana de alerta
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              La alerta diaria por correo mostrará los cheques que vencen en los próximos <b>N</b> días.
            </p>
            <div>
              <Label>Días hacia adelante (1–90)</Label>
              <Input
                type="number"
                min="1"
                max="90"
                value={ventanaDias}
                onChange={(e) => setVentanaDias(e.target.value)}
                data-testid="input-ventana-dias"
              />
              <p className="text-xs text-muted-foreground mt-1">Default: 14 días. Recomendado: 7–21.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)} disabled={savingConfig}>Cancelar</Button>
            <Button onClick={saveVentana} disabled={savingConfig} className="gap-2" data-testid="btn-save-ventana">
              <Save className="h-4 w-4" />
              {savingConfig ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
